/**
 * Which announcement attachments may be opened in a browser tab.
 *
 * "View" opens a blob: URL, and a blob: URL inherits the origin of the app. Anything the
 * browser renders actively (HTML, SVG, XML ...) would therefore run with access to the
 * reader's session, so only formats that can only be displayed passively are viewable.
 * Everything else, including SVG, HTML and unknown types, is download-only.
 *
 * A file must pass on BOTH signals - the content type the upload was stored with and the
 * file extension - so a mislabelled "photo.png" that is really HTML is not viewable.
 */
const VIEWABLE_EXTENSIONS_BY_CONTENT_TYPE: Readonly<Record<string, readonly string[]>> = {
  'application/pdf': ['pdf'],
  'image/png': ['png'],
  'image/jpeg': ['jpg', 'jpeg'],
  'image/gif': ['gif'],
  'image/webp': ['webp']
};

/**
 * The canonical allowlisted content type to view this file as, or null when it is
 * download-only. Callers re-type the downloaded blob to this value, so the tab can only
 * ever hold a PDF or a raster image, whatever Content-Type the server or a proxy sent.
 */
export function viewableContentType(contentType: string | null | undefined, fileName: string | null | undefined): string | null {
  const type = (contentType ?? '').split(';')[0].trim().toLowerCase();
  const allowedExtensions = VIEWABLE_EXTENSIONS_BY_CONTENT_TYPE[type];
  if (!allowedExtensions) return null;

  const name = (fileName ?? '').trim().toLowerCase();
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return null;
  return allowedExtensions.includes(name.substring(dot + 1)) ? type : null;
}

export function canViewAttachmentInBrowser(contentType: string | null | undefined, fileName: string | null | undefined): boolean {
  return viewableContentType(contentType, fileName) !== null;
}
